import React, { useState } from "react";
import { Country, State } from "country-state-city";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import axios from "axios";
import { server } from "../../server";
import { backend_url } from "../../server";
import { toast } from "react-toastify";
import { getProductImageUrl } from "../../utils/mediaUtils";
import {
  FiMapPin,
  FiUser,
  FiMail,
  FiPhone,
  FiHome,
  FiTag,
  FiCreditCard,
  FiCheck,
  FiTruck,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi";
import PincodeService from "../PincodeService/PincodeService";
import { usePincodeService } from "../../hooks/usePincodeService";
import DeliveryChoice from "./DeliveryChoice";
import { useCurrency } from "../../context/CurrencyContext";

const Checkout = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { cart } = useSelector((state) => state.cart);
  const { formatPrice, currency } = useCurrency();
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [userInfo, setUserInfo] = useState(false);
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [zipCode, setZipCode] = useState(null);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponCodeData, setCouponCodeData] = useState(null);
  const [discountPrice, setDiscountPrice] = useState(null);
  const [buyNowOrder, setBuyNowOrder] = useState(null);
  const [showPincodeService, setShowPincodeService] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [shopShippingConfigs, setShopShippingConfigs] = useState({});

  // District-based delivery state (for Positive Moments single-vendor)
  const [deliverySelection, setDeliverySelection] = useState(null);
  const [useDistrictDelivery, setUseDistrictDelivery] = useState(false);
  const [vendorDeliveryConfig, setVendorDeliveryConfig] = useState(null);

  // Use Buy Now order data if available, otherwise use cart
  const activeCart = buyNowOrder ? buyNowOrder.cart : cart;

  // Get the single shop ID from cart (single-vendor checkout)
  const singleShopId =
    activeCart && activeCart.length > 0
      ? [...new Set(activeCart.map((item) => {
          const sid = item.shopId || item.shop;
          return sid?._id || sid;
        }))][0]
      : null;

  // Fetch actual vendor delivery config from API
  useEffect(() => {
    if (!singleShopId) return;

    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${server}/vendor-delivery/config/${singleShopId}`);
        if (res.data.success) {
          setVendorDeliveryConfig(res.data.config);
          setUseDistrictDelivery(true);
          
          // Auto-select method based on what's enabled
          const config = res.data.config;
          if (config.deliveryEnabled && !config.pickupEnabled) {
            setDeliverySelection({
              method: "DELIVERY",
              districtId: null,
              districtName: null,
              fee: 0,
              isValid: false,
            });
          } else {
            // Default to Pickup/Collect if enabled or as fallback
            setDeliverySelection({
              method: "COLLECT",
              districtId: null,
              districtName: null,
              fee: 0,
              isValid: config.pickupEnabled !== false,
            });
          }
        }
      } catch (err) {
        console.error("Failed to load vendor delivery config:", err);
        // Fallback to collect-only defaults
        setUseDistrictDelivery(true);
        setVendorDeliveryConfig({
          hasNoConfig: true,
          deliveryEnabled: false,
          pickupEnabled: true,
          districtFees: [],
        });
        setDeliverySelection({
          method: "COLLECT",
          districtId: null,
          districtName: null,
          fee: 0,
          isValid: true,
        });
      }
    };

    fetchConfig();
  }, [singleShopId]);

  // shopShippingConfigs intentionally left empty — per-shop shipping config via API not active
  // Shipping cost falls back to product-level shipping rates or deliveryInfo.shippingCharge

  const { validatePincode } = usePincodeService();

  // Validate pincode when manually entered
  const handleZipCodeChange = async (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only numbers
    if (value.length <= 6) {
      setZipCode(value);

      // Clear previous delivery info if pincode is being changed
      if (value !== deliveryInfo?.pincode) {
        setDeliveryInfo(null);
      }

      // Auto-validate when 6 digits are entered
      if (value.length === 6) {
        try {
          const result = await validatePincode(value);
          if (result.isValid && result.data) {
            setDeliveryInfo({
              ...result.data,
              deliveryAvailable: true,
            });
            setCountry("IN");
            setCity(result.data.district || "");
            setLatitude(result.data.latitude || "");
            setLongitude(result.data.longitude || "");
            toast.success(result.message);
          } else {
            setDeliveryInfo(null);
            toast.error(
              result.message || "Delivery not available for this pincode"
            );
          }
        } catch (error) {
          console.error("Error validating pincode:", error);
          setDeliveryInfo(null);
        }
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // Auto-fill default or latest address if it exists
    if (user && user.addresses && user.addresses.length > 0) {
      const targetAddress = user.addresses.find((addr) => addr.isDefault) || user.addresses[user.addresses.length - 1];
      setAddress1(targetAddress.address1 || "");
      setAddress2(targetAddress.address2 || "");
      setCity(targetAddress.city || "");
      setCountry(targetAddress.country || "IN");
      setZipCode(targetAddress.zipCode || "");
      setUserInfo(true);
    }
    
    // Check for Buy Now order data in localStorage
    const storedOrder = localStorage.getItem("latestOrder");
    if (storedOrder) {
      try {
        const orderData = JSON.parse(storedOrder);
        console.log("Found buyNowOrder in localStorage:", orderData);

        setBuyNowOrder(orderData);
      } catch (error) {
        console.error("Error parsing stored order:", error);
        localStorage.removeItem("latestOrder");
      }
    }
  }, [cart, user]);

  const paymentSubmit = () => {
    // ===== SINGLE SELLER VALIDATION =====
    // Check if cart has items from multiple sellers
    const uniqueShopIds = [...new Set(activeCart.map((item) => {
      const sid = item.shopId || item.shop;
      return sid?._id || sid;
    }))];

    if (uniqueShopIds.length > 1) {
      // Single-vendor store — this shouldn't happen in normal flow
      toast.error("Cart contains mixed items. Please refresh and try again.");
      return;
    }

    if (uniqueShopIds.length === 0) {
      toast.error("Your cart is empty!");
      return;
    }

    // Get the single seller's shop ID
    const singleShopId = uniqueShopIds[0];

    // ===== DISTRICT-BASED DELIVERY VALIDATION (Positive Moments) =====
    if (useDistrictDelivery) {
      // Validate delivery selection
      if (!deliverySelection) {
        toast.error("Please select a delivery method (Collect or Delivery)!");
        return;
      }

      if (!deliverySelection.isValid) {
        toast.error("Please complete your delivery selection!");
        return;
      }

      if (
        deliverySelection.method === "DELIVERY" &&
        !deliverySelection.districtId
      ) {
        toast.error("Please select a delivery district!");
        return;
      }

      if (deliverySelection.method === "DELIVERY") {
        if (!address1 || !city || !zipCode) {
          toast.error("Please fill in your delivery address details (Address Line 1, City, and Postal Code)!");
          return;
        }
      }

      // For COLLECT method, we need minimal address info for order records
      // For DELIVERY method, we need the district info

      const shippingAddress = {
        address1:
          address1 || (deliverySelection.method === "COLLECT" ? "Pickup" : ""),
        address2: address2 || "",
        zipCode: zipCode || "",
        country: country || "",
        city: city || deliverySelection.districtName || "",
        latitude,
        longitude,
        // District-based delivery info
        deliveryInfo: {
          method: deliverySelection.method,
          districtId: deliverySelection.districtId,
          districtName: deliverySelection.districtName,
          districtCode: deliverySelection.districtCode,
          deliveryFee: deliverySelection.deliveryFee,
          estimatedDays: deliverySelection.estimatedDays,
        },
      };

      // Calculate final shipping (delivery fee)
      const finalShipping =
        deliverySelection.method === "COLLECT"
          ? 0
          : deliverySelection.deliveryFee;

      // Calculate final total
      const finalTotalPrice = couponCodeData
        ? Number(subTotalPrice + finalShipping - discountPrice).toFixed(2)
        : Number(subTotalPrice + finalShipping).toFixed(2);

      const orderData = {
        cart: activeCart,
        totalPrice: finalTotalPrice,
        subTotalPrice,
        shipping: finalShipping,
        discountPrice,
        tax: 0,
        shippingAddress,
        user,
        shopId: singleShopId,
        // NEW: District delivery fields
        deliveryMethod: deliverySelection.method,
        deliveryDistrict:
          deliverySelection.method === "DELIVERY"
            ? {
                districtId: deliverySelection.districtId,
                districtName: deliverySelection.districtName,
                districtCode: deliverySelection.districtCode,
              }
            : null,
        deliveryFeeAmount: finalShipping,
        deliveryProviderType: "VENDOR",
        pickupDetails:
          deliverySelection.method === "COLLECT"
            ? {
                pickupAddress: deliverySelection.pickupAddress,
                pickupInstructions: deliverySelection.pickupInstructions,
              }
            : null,
      };

      localStorage.setItem("latestOrder", JSON.stringify(orderData));

      // Clear Buy Now order data after creating final order
      if (buyNowOrder) {
        setBuyNowOrder(null);
      }

      navigate("/payment");
      return;
    }

    // ===== LEGACY PINCODE-BASED DELIVERY (Karnataka/India) =====
    // Basic form validation
    if (
      address1 === "" ||
      zipCode === null ||
      country === "" ||
      city === ""
    ) {
      toast.error("Please choose your delivery address!");
      return;
    }

    // Pincode validation - must be 6 digits
    if (!zipCode || zipCode.length !== 6 || !/^\d{6}$/.test(zipCode)) {
      toast.error("Please enter a valid 6-digit pincode!");
      return;
    }

    // Check if delivery is available for this pincode
    if (!deliveryInfo || !deliveryInfo.deliveryAvailable) {
      toast.error(
        "Delivery is not available for this pincode. Please check delivery location first!"
      );
      setShowPincodeService(true); // Open pincode service modal
      return;
    }

    // Validate if the pincode is in India (our serviceable area)
    if (
      deliveryInfo.country &&
      !(deliveryInfo.country.toLowerCase().includes("india") || deliveryInfo.country.toLowerCase() === "in")
    ) {
      toast.error(
        "We currently deliver only within India. Please select an Indian address!"
      );
      setShowPincodeService(true);
      return;
    }

    // Additional validation - check if the entered zipCode matches validated pincode
    if (zipCode !== deliveryInfo.pincode) {
      toast.error(
        "The entered pincode doesn't match the validated delivery location. Please verify!"
      );
      setShowPincodeService(true);
      return;
    }

    const shippingAddress = {
      address1,
      address2,
      zipCode,
      country,
      city,
      latitude,
      longitude,
      // Add delivery info for order tracking
      deliveryInfo: {
        area: deliveryInfo.area,
        district: deliveryInfo.district,
        state: deliveryInfo.state,
        estimatedDeliveryDays: deliveryInfo.estimatedDeliveryDays,
        shippingCharge: deliveryInfo.shippingCharge,
      },
    };

    const orderData = {
      cart: activeCart,
      totalPrice,
      subTotalPrice,
      shipping,
      discountPrice,
      tax: 0,
      shippingAddress,
      user,
      shopId: singleShopId, // Add the single seller's shop ID
    };

    localStorage.setItem("latestOrder", JSON.stringify(orderData));

    // Clear Buy Now order data after creating final order
    if (buyNowOrder) {
      setBuyNowOrder(null);
    }

    navigate("/payment");
  };

  const subTotalPrice = buyNowOrder
    ? buyNowOrder.subTotalPrice
    : cart.reduce((acc, item) => {
        // Use finalPrice if available (for attribute variations), otherwise use discountPrice
        const price =
          item.finalPrice ||
          item.discountPrice ||
          item.originalPrice ||
          item.price ||
          0;
        return acc + item.qty * price;
      }, 0);

  // Calculate shipping based on delivery info or use simple shipping calculation
  const shipping = React.useMemo(() => {
    // NEW: District-based delivery shipping
    if (useDistrictDelivery && deliverySelection) {
      if (deliverySelection.method === "COLLECT") {
        return 0;
      }
      return deliverySelection.deliveryFee || 0;
    }

    // For cart items, calculate simple shipping based on shops
    if (!buyNowOrder && activeCart && activeCart.length > 0) {
      const shopGroups = activeCart.reduce((acc, item) => {
        const shopId = item.shopId || (item.shop && item.shop._id);
        if (!acc[shopId]) {
          acc[shopId] = {
            items: [],
            totalValue: 0,
            shop: item.shop,
          };
        }
        const price =
          item.finalPrice ||
          item.discountPrice ||
          item.originalPrice ||
          item.price ||
          0;
        acc[shopId].items.push(item);
        acc[shopId].totalValue += price * item.qty;
        return acc;
      }, {});

      // Calculate total shipping with product-specific configurations
      let totalShipping = 0;

      Object.entries(shopGroups).forEach(([shopId, group]) => {
        const shop = group.shop || {};
        
        // Get shipping configuration for this shop
        const shippingConfig = shopShippingConfigs[shopId];
        let shopShippingCost = 0;
        let hasProductSpecificShipping = false;

        // Calculate shipping for each product in this shop
        group.items.forEach((item) => {
          const productShipping = item.shipping;
          let productShippingCost = 0;

          // Check if product has specific shipping configuration
          if (productShipping && productShipping.baseShippingRate > 0) {
            hasProductSpecificShipping = true;

            // Check product-level free shipping threshold
            const itemTotal =
              (item.finalPrice ||
                item.discountPrice ||
                item.originalPrice ||
                item.price ||
                0) * item.qty;
            if (
              productShipping.freeShippingThreshold &&
              itemTotal >= productShipping.freeShippingThreshold
            ) {
              productShippingCost = 0;
            } else {
              productShippingCost = productShipping.baseShippingRate * item.qty;
            }
          } else if (shippingConfig) {
            // Use shop-level configuration for products without specific shipping

            // Check shop-level free shipping threshold for this product
            const itemTotal =
              (item.finalPrice ||
                item.discountPrice ||
                item.originalPrice ||
                item.price ||
                0) * item.qty;
            if (
              shippingConfig.freeShippingThreshold &&
              itemTotal >= shippingConfig.freeShippingThreshold
            ) {
              productShippingCost = 0;
            } else {
              const shopShippingRate =
                shippingConfig.baseShippingRate !== undefined
                  ? shippingConfig.baseShippingRate
                  : shippingConfig.shippingCharge !== undefined
                  ? shippingConfig.shippingCharge
                  : 30;
              productShippingCost = shopShippingRate * item.qty;
            }
          } else {
            // Fallback to default shipping
            const itemTotal =
              (item.finalPrice ||
                item.discountPrice ||
                item.originalPrice ||
                item.price ||
                0) * item.qty;
            if (itemTotal >= 999) {
              productShippingCost = 0;
            } else {
              productShippingCost = 30 * item.qty;
            }
          }

          shopShippingCost += productShippingCost;
        });

        // Check if entire shop order qualifies for free shipping (only if no product-specific shipping)
        if (
          !hasProductSpecificShipping &&
          shippingConfig &&
          shippingConfig.freeShippingThreshold &&
          group.totalValue >= shippingConfig.freeShippingThreshold
        ) {
          shopShippingCost = 0;
        }

        totalShipping += shopShippingCost;
      });

      return totalShipping;
    }

    // Fallback to old static calculation for Buy Now orders or when dynamic fails
    if (deliveryInfo && deliveryInfo.shippingCharge !== undefined) {
      // Free shipping for orders above ₹999
      return subTotalPrice >= 999 ? 0 : deliveryInfo.shippingCharge;
    }

    // Default fallback for buy now orders
    return buyNowOrder ? buyNowOrder.shipping : subTotalPrice * 0.1;
  }, [
    deliveryInfo,
    subTotalPrice,
    buyNowOrder,
    activeCart,
    shopShippingConfigs,
    useDistrictDelivery,
    deliverySelection,
  ]);

  // Handle delivery selection from DeliveryChoice component
  // Wrapped in useCallback to prevent infinite re-renders
  const handleDeliverySelect = React.useCallback((selection) => {
    setDeliverySelection(selection);
  }, []);

  // Handle pincode validation and location selection
  const handleLocationSelect = async (locationData) => {
    setAddress1(locationData.address1 || "");
    setAddress2(locationData.address2 || "");
    setCity(locationData.city || "");
    setCountry(locationData.country || "IN");
    setZipCode(locationData.zipCode || "");
    setLatitude(locationData.latitude || "");
    setLongitude(locationData.longitude || "");

    // Validate the pincode for delivery info (crucial for checkout)
    if (locationData.zipCode) {
      try {
        const result = await validatePincode(locationData.zipCode.toString());
        if (result.isValid && result.data) {
          setDeliveryInfo({
            ...result.data,
            deliveryAvailable: true,
          });
          toast.success(result.message || "Delivery location confirmed");
        } else {
          setDeliveryInfo(null);
          toast.error(
            result.message || "Delivery not available for this location"
          );
        }
      } catch (error) {
        console.error("Error validating saved address pincode:", error);
        setDeliveryInfo(null);
        toast.error("Error validating delivery location");
      }
    }

    setShowPincodeService(false);
  };

  const handlePincodeValidate = (pincodeData) => {
    setDeliveryInfo(pincodeData);
    setCountry("IN"); // India
    setCity(pincodeData.district || "");
    setLatitude(pincodeData.latitude || "");
    setLongitude(pincodeData.longitude || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = couponCode;

    try {
      const { data } = await axios.post(`${server}/coupon/validate`, {
        couponCode: name,
        checkoutItems: activeCart,
      });

      if (data.success) {
        setDiscountPrice(data.discount);
        setCouponCodeData({
          name: data.couponName,
          _id: data.couponId,
          value: data.couponCodeValue,
          discountType: data.couponDiscountType,
        });
        setCouponCode("");
        toast.success(`Coupon "${data.couponName}" applied successfully!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid or expired coupon code!");
      setCouponCode("");
    }
  };

  // Handle coupon application from suggestions
  const handleApplyCouponFromSuggestion = async (couponName) => {
    try {
      const { data } = await axios.post(`${server}/coupon/validate`, {
        couponCode: couponName,
        checkoutItems: activeCart,
      });

      if (data.success) {
        setDiscountPrice(data.discount);
        setCouponCodeData({
          name: data.couponName,
          _id: data.couponId,
          value: data.couponCodeValue,
          discountType: data.couponDiscountType,
        });
        setCouponCode("");
        toast.success(`Coupon "${data.couponName}" applied successfully!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid or expired coupon code!");
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    setDiscountPrice(null);
    setCouponCodeData(null);
    setCouponCode("");
    toast.success("Coupon removed successfully!");
  };

  const discountPercentenge = couponCodeData ? discountPrice : "";

  // Calculate total price
  const totalPrice = couponCodeData
    ? Number(subTotalPrice + shipping - discountPercentenge).toFixed(2)
    : Number(subTotalPrice + shipping).toFixed(2);

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row font-sans">
      {/* Left Column - Delivery/Shipping Information (White Background) */}
      <div className="w-full lg:w-[55%] xl:w-[60%] bg-white px-4 py-8 lg:px-12 xl:px-24 border-r border-gray-200">
        <div className="max-w-2xl mx-auto lg:ml-auto lg:mr-0 space-y-8">
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Checkout</h1>
          
          <div className="space-y-8">
            {/* District-based Delivery Choice (Positive Moments) */}
            {useDistrictDelivery && singleShopId && (
              <DeliveryChoice
                shopId={singleShopId}
                cartTotal={subTotalPrice}
                onDeliverySelect={handleDeliverySelect}
                initialMethod={deliverySelection?.method}
                initialDistrict={deliverySelection?.districtId}
                shippingAddress={{ address1, address2, city, zipCode, country }}
                onEditAddress={() => window.location.href = '/profile?tab=address'}
              />
            )}

            {/* Show address display or form based on delivery method */}
            {useDistrictDelivery ? (
              // For district delivery, show simplified address only when DELIVERY is selected
              deliverySelection?.method === "DELIVERY" && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="bg-white px-6 py-5 border-b border-gray-100 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
                        <FiMapPin className="w-5 h-5 text-gray-700" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                          Delivery Address
                        </h2>
                        <p className="text-gray-500 text-sm mt-0.5">
                          {userInfo ? "Using saved address" : "Add specific address details"}
                        </p>
                      </div>
                    </div>
                    {user && user.addresses && user.addresses.length > 0 && (
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => window.location.href = '/profile?tab=address'}
                          className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 border border-gray-200 shadow-sm"
                        >
                          Select Another Address
                        </button>
                        <button 
                          onClick={() => setUserInfo(!userInfo)}
                          className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 border border-gray-200 shadow-sm"
                        >
                          {userInfo ? "Edit Address" : "Use Saved"}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-6 space-y-4">
                    {userInfo ? (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <div className="flex items-start space-x-3">
                          <FiMapPin className="w-5 h-5 text-indigo-600 mt-0.5" />
                          <div>
                            <h3 className="font-medium text-gray-900 mb-1">Delivering To</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {address1}
                              {address2 && `, ${address2}`}
                              <br />
                              {city}{city && country ? ", " : ""}{country}
                              {zipCode && ` - ${zipCode}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Address Line 1
                            </label>
                            <input
                              type="text"
                              value={address1}
                              onChange={(e) => setAddress1(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Street address, apartment, unit"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Address Line 2
                            </label>
                            <input
                              type="text"
                              value={address2}
                              onChange={(e) => setAddress2(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Building, landmark"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              City/Town
                            </label>
                            <input
                              type="text"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="City or town name"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Postal Code
                            </label>
                            <input
                              type="text"
                              value={zipCode || ""}
                              onChange={(e) => setZipCode(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Postal/ZIP code"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      <FiTruck className="inline mr-1" />
                      Your order will be delivered to the{" "}
                      {deliverySelection?.districtName} district
                    </p>
                  </div>
                </div>
              )
            ) : (
              // Legacy pincode-based shipping form for India
              <ShippingInfo
                user={user}
                country={country}
                setCountry={setCountry}
                city={city}
                setCity={setCity}
                userInfo={userInfo}
                setUserInfo={setUserInfo}
                address1={address1}
                setAddress1={setAddress1}
                address2={address2}
                setAddress2={setAddress2}
                zipCode={zipCode}
                onZipCodeChange={handleZipCodeChange}
                latitude={latitude}
                setLatitude={setLatitude}
                longitude={longitude}
                setLongitude={setLongitude}
                onShowPincodeService={() => setShowPincodeService(true)}
                deliveryInfo={deliveryInfo}
                handleZipCodeValidation={handleZipCodeChange}
                useDistrictDelivery={useDistrictDelivery}
              />
            )}
          </div>
        </div>
      </div>

      {/* Right Column - Order Summary (Light Gray Background) */}
      <div className="w-full lg:w-[45%] xl:w-[40%] bg-[#fafafa] px-4 py-8 lg:px-12 xl:px-16 border-t lg:border-t-0 border-gray-200">
        <div className="max-w-md mx-auto lg:mr-auto lg:ml-0 sticky top-8">
          <CartData
              handleSubmit={handleSubmit}
              totalPrice={totalPrice}
              shipping={shipping}
              subTotalPrice={subTotalPrice}
              couponCode={couponCode}
              setCouponCode={setCouponCode}
              discountPercentenge={discountPercentenge}
              paymentSubmit={paymentSubmit}
              deliveryInfo={deliveryInfo}
              activeCart={activeCart}
              handleApplyCouponFromSuggestion={handleApplyCouponFromSuggestion}
              couponCodeData={couponCodeData}
              handleRemoveCoupon={handleRemoveCoupon}
              useDistrictDelivery={useDistrictDelivery}
              deliverySelection={deliverySelection}
            />
        </div>
      </div>

      {/* Pincode Service Modal - Only for legacy system */}
      {!useDistrictDelivery && (
        <PincodeService
          show={showPincodeService}
          onClose={() => setShowPincodeService(false)}
          onLocationSelect={handleLocationSelect}
          onPincodeValidate={handlePincodeValidate}
          savedAddresses={user?.addresses || []}
        />
      )}
    </div>
  );
};

const ShippingInfo = ({
  user,
  country,
  setCountry,
  city,
  setCity,
  userInfo,
  setUserInfo,
  address1,
  setAddress1,
  address2,
  setAddress2,
  zipCode,
  onZipCodeChange,
  latitude,
  setLatitude,
  longitude,
  setLongitude,
  onShowPincodeService,
  deliveryInfo,
  handleZipCodeValidation,
  useDistrictDelivery,
}) => {
  const { formatPrice } = useCurrency();
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-white px-6 py-5 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
            <FiMapPin className="w-5 h-5 text-gray-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
              Delivery Address
            </h2>
            <p className="text-gray-500 text-sm mt-0.5">
              {userInfo ? "Using saved address" : "Where should we deliver your order?"}
            </p>
          </div>
        </div>
        {user && user.addresses && user.addresses.length > 0 && (
          <button 
            onClick={() => setUserInfo(!userInfo)}
            className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 border border-gray-200 shadow-sm"
          >
            {userInfo ? "Edit Address" : "Use Saved"}
          </button>
        )}
      </div>

      {/* Form Content */}
      <div className="p-6">
        {userInfo ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
            <div>
              <p className="text-sm text-gray-500 mb-1">Full Name</p>
              <p className="font-semibold text-gray-800">{user?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Phone Number</p>
              <p className="font-semibold text-gray-800">{user?.phoneNumber}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-500 mb-1">Delivery Address</p>
              <p className="font-medium text-gray-800">
                {address1} {address2 ? `, ${address2}` : ""}
              </p>
              <p className="text-gray-600 mt-1">
                {city}, {country} - {zipCode}
              </p>
            </div>
          </div>
        ) : (
          <form className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FiUser className="w-5 h-5 mr-2 text-indigo-600" />
              Personal Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={user && user.name}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={user && user.email}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="Enter your email"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number *
                </label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    required
                    value={user && user.phoneNumber}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Zip Code *
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={zipCode || ""}
                    onChange={onZipCodeChange}
                    required
                    maxLength={6}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="Enter 6-digit pincode"
                  />
                  <button
                    type="button"
                    onClick={onShowPincodeService}
                    className="w-full py-2 px-3 text-sm bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                  >
                    <FiMapPin className="w-4 h-4 inline mr-2" />
                    Check Delivery Location
                  </button>

                  {/* Delivery Info Display */}
                  {deliveryInfo && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <FiCheck className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-green-800">
                            Delivery Available
                          </p>
                          <p className="text-green-700">
                            {deliveryInfo.area}, {deliveryInfo.district}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Estimated delivery:{" "}
                            {deliveryInfo.estimatedDeliveryDays} days |
                            Shipping: {formatPrice(deliveryInfo.shippingCharge)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <FiMapPin className="w-5 h-5 mr-2 text-indigo-600" />
              Location Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Country *
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">Select your country</option>
                  {Country &&
                    Country.getAllCountries().map((item) => (
                      <option key={item.isoCode} value={item.isoCode}>
                        {item.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  State/City *
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                >
                  <option value="">Select your state/city</option>
                  {State &&
                    State.getStatesOfCountry(country).map((item) => (
                      <option key={item.isoCode} value={item.isoCode}>
                        {item.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Address Line 1 *
                </label>
                <div className="relative">
                  <FiHome className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    required
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="House number, street name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Address Line 2 *
                </label>
                <input
                  type="text"
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  placeholder="Apartment, suite, building"
                />
              </div>
            </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const CartData = ({
  handleSubmit,
  totalPrice,
  shipping,
  subTotalPrice,
  couponCode,
  setCouponCode,
  discountPercentenge,
  paymentSubmit,
  deliveryInfo,
  activeCart,
  handleApplyCouponFromSuggestion,
  couponCodeData,
  handleRemoveCoupon,
  useDistrictDelivery,
  deliverySelection,
}) => {
  const { formatPrice, currency } = useCurrency();

  // Determine if checkout can proceed
  const canProceed = useDistrictDelivery
    ? deliverySelection && deliverySelection.isValid
    : deliveryInfo && deliveryInfo.deliveryAvailable;

  return (
    <div className="sticky top-6 space-y-4">
      {/* Order Summary Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-white px-5 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-50 rounded flex items-center justify-center border border-gray-100">
              <FiCreditCard className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Order Summary
              </h2>
            </div>
          </div>
        </div>

        {/* Summary Content */}
        <div className="p-4 space-y-4">
          {/* Order Items */}
          {activeCart && activeCart.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-800 border-b pb-2">
                Order Items ({activeCart.length})
              </h3>
              {activeCart.map((item, index) => {
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-2 bg-gray-50 rounded-lg"
                  >
                    <img
                      src={
                        getProductImageUrl(item?.images, 0, backend_url) ||
                        "https://placehold.co/48x48/E5E7EB/6B7280?text=No+Image"
                      }
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-md flex-shrink-0 bg-gray-200"
                      onError={(e) => {
                        e.target.src =
                          "https://placehold.co/48x48/E5E7EB/6B7280?text=No+Image";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-800 truncate">
                        {item.name}
                      </h4>
                      <div className="text-xs text-gray-600">
                        Qty: {item.qty}
                      </div>

                      {/* Selected Attributes */}
                      {item.selectedAttributes &&
                        Object.keys(item.selectedAttributes).length > 0 && (
                          <div className="mt-1">
                            {Object.entries(item.selectedAttributes).map(
                              ([key, value]) => (
                                <span
                                  key={key}
                                  className="inline-block mr-2 mb-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full"
                                >
                                  {key}: {value}
                                </span>
                              )
                            )}
                          </div>
                        )}

                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs text-gray-600">
                          {item.finalPrice &&
                          item.finalPrice !==
                            (item.discountPrice ||
                              item.originalPrice ||
                              item.price) ? (
                            <div>
                              <span className="line-through text-gray-400">
                                {formatPrice(
                                  item.discountPrice ||
                                    item.originalPrice ||
                                    item.price
                                )}
                              </span>
                              <span className="ml-1 text-green-600 font-semibold">
                                {formatPrice(item.finalPrice)}
                              </span>
                              <span className="block text-xs text-green-600">
                                Attribute price applied
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm font-semibold text-gray-800">
                              {formatPrice(
                                item.finalPrice ||
                                  item.discountPrice ||
                                  item.originalPrice ||
                                  item.price
                              )}
                            </span>
                          )}
                          <span className="ml-1 text-gray-600">
                            × {item.qty}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-indigo-600">
                          {formatPrice(
                            (item.finalPrice ||
                              item.discountPrice ||
                              item.originalPrice ||
                              item.price) * item.qty
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Price Breakdown */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Subtotal</span>
              <span className="font-semibold text-gray-800">
                {formatPrice(subTotalPrice)}
              </span>
            </div>

            {/* Dynamic Shipping Breakdown */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Shipping & Handling</span>
              <span className="font-semibold text-gray-800">
                {shipping === 0 ? "FREE" : formatPrice(shipping)}
              </span>
            </div>

            {/* Free Shipping Message */}
            {shipping === 0 && subTotalPrice >= 999 && !useDistrictDelivery && (
              <div className="text-xs text-green-600 -mt-2">
                🎉 Free shipping on orders above {formatPrice(999)}
              </div>
            )}

            {/* District-based Delivery Info */}
            {useDistrictDelivery && deliverySelection && (
              <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
                <div className="flex items-center space-x-2">
                  <FiTruck className="w-3 h-3" />
                  <span className="font-medium">
                    {deliverySelection.method === "COLLECT"
                      ? "Pickup from store"
                      : `Delivery to ${deliverySelection.districtName}`}
                  </span>
                </div>
                {deliverySelection.method === "DELIVERY" &&
                  deliverySelection.estimatedDays && (
                    <div className="mt-1">
                      Estimated delivery: {deliverySelection.estimatedDays}{" "}
                      business day(s)
                    </div>
                  )}
                {deliverySelection.method === "COLLECT" &&
                  deliverySelection.pickupAddress && (
                    <div className="mt-1 text-gray-500">
                      Pickup: {deliverySelection.pickupAddress}
                    </div>
                  )}
              </div>
            )}

            {/* Legacy Pincode Delivery Info */}
            {!useDistrictDelivery && deliveryInfo && (
              <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
                <div className="flex items-center space-x-2">
                  <FiMapPin className="w-3 h-3" />
                  <span>
                    Delivery to {deliveryInfo.area}, {deliveryInfo.district}
                  </span>
                </div>
                <div className="mt-1">
                  Estimated delivery: {deliveryInfo.estimatedDeliveryDays} days
                </div>
              </div>
            )}

            {discountPercentenge && (
              <div className="flex justify-between items-center text-green-600">
                <span className="flex items-center text-sm">
                  <FiTag className="w-3 h-3 mr-1" />
                  Discount Applied
                </span>
                <span className="font-semibold">
                  -{formatPrice(discountPercentenge)}
                </span>
              </div>
            )}

            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-800">
                  Total
                </span>
                <span className="text-xl font-bold text-indigo-600">
                  {formatPrice(totalPrice)}
                </span>
              </div>
            </div>
          </div>

          {/* CouponSuggestions has been removed */}

          {/* Manual Coupon Code */}
          <div className="space-y-2">
            <h3 className="font-medium text-gray-800 flex items-center text-sm">
              <FiTag className="w-4 h-4 mr-1 text-indigo-600" />
              Have a specific coupon code?
            </h3>

            <div className="space-y-2">
              <div className="relative">
                <FiTag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit({ preventDefault: () => {} });
                    }
                  }}
                  required
                />
              </div>

              <button
                type="button"
                onClick={() => handleSubmit({ preventDefault: () => {} })}
                disabled={!couponCode}
                className="w-full h-[42px] flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-sm hover:shadow"
              >
                Apply Code
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Proceed to Payment Button */}
      <button
        onClick={paymentSubmit}
        disabled={!canProceed}
        className={`w-full py-4 font-semibold text-base rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 ${
          canProceed
            ? "bg-gray-900 hover:bg-black text-white shadow hover:shadow-md"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        {canProceed ? (
          <>
            <FiCheck className="w-4 h-4" />
            <span>Proceed to Payment</span>
          </>
        ) : (
          <>
            <FiMapPin className="w-4 h-4" />
            <span>
              {useDistrictDelivery
                ? "Select Delivery Method"
                : "Please Verify Delivery Location"}
            </span>
          </>
        )}
      </button>

      {/* Delivery Validation Message - District-based */}
      {useDistrictDelivery && !deliverySelection && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <FiTruck className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 text-sm">
                Delivery Method Required
              </h4>
              <p className="text-yellow-700 text-xs mt-1">
                Please choose between Collect (pickup) or Delivery above to
                proceed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Validation Message - Legacy pincode */}
      {!useDistrictDelivery && !deliveryInfo && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <FiMapPin className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 text-sm">
                Delivery Location Required
              </h4>
              <p className="text-yellow-700 text-xs mt-1">
                Please enter a valid Karnataka pincode to check delivery
                availability before proceeding to payment.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Security Note */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <div className="flex items-start space-x-2">
          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <FiCheck className="w-3 h-3 text-green-600" />
          </div>
          <div>
            <h4 className="font-medium text-green-800 text-sm">
              Secure Checkout
            </h4>
            <p className="text-xs text-green-700 mt-0.5">
              Your payment information is encrypted and secure. We never store
              your card details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
